import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';
import { useAuth } from '@/context/AuthContext';

export default function Login() {
  const { isDark } = useTheme();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const { error: loginError } = await login(email, password);

    setIsLoading(false);

    if (loginError) {
      setError(loginError);
      return;
    }

    navigate('/');
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isDark ? 'bg-dark-bg' : 'bg-light-bg'}`}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/8d501578-6f11-4960-92e0-74720553c4c2.png"
            alt="EmlakPro Logo"
            className="w-20 h-20 object-contain mb-4"
          />
          <h1 className={`text-2xl font-bold ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            EmlakPro
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
            Emlak Danışmanı Paneli
          </p>
        </div>

        {/* Login Card */}
        <div
          className={`rounded-2xl p-6 md:p-8 shadow-soft ${
            isDark ? 'bg-dark-card border border-dark-border' : 'bg-white border border-light-border'
          }`}
        >
          <h2 className={`text-lg font-semibold mb-6 ${isDark ? 'text-dark-text' : 'text-light-text'}`}>
            Giriş Yap
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                E-posta
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-mail-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="ornek@email.com"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
                Şifre
              </label>
              <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors ${
                isDark
                  ? 'bg-dark-bg border-dark-border focus-within:border-primary-500'
                  : 'bg-light-bg border-light-border focus-within:border-primary-500'
              }`}>
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`ri-lock-line ${isDark ? 'text-dark-muted' : 'text-light-muted'}`} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className={`flex-1 bg-transparent text-sm outline-none ${
                    isDark ? 'text-dark-text placeholder:text-dark-muted' : 'text-light-text placeholder:text-light-muted'
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`w-5 h-5 flex items-center justify-center ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            {/* Remember & Forgot */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-500" />
                <span className={`text-sm ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>Beni hatırla</span>
              </label>
              <button type="button" className="text-sm text-primary-500 hover:text-primary-600 font-medium">
                Şifremi unuttum
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-login-box-line" />
                  </div>
                  Giriş Yap
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6">
            <div className={`flex-1 h-px ${isDark ? 'bg-dark-border' : 'bg-light-border'}`} />
            <span className={`text-xs ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>veya</span>
            <div className={`flex-1 h-px ${isDark ? 'bg-dark-border' : 'bg-light-border'}`} />
          </div>

          {/* Register instead */}
          <Link
            to="/register"
            className={`w-full py-2.5 px-4 font-medium rounded-lg border text-center block transition-colors ${
              isDark
                ? 'border-dark-border text-dark-text hover:bg-dark-border'
                : 'border-light-border text-light-text hover:bg-light-bg'
            }`}
          >
            Yeni Hesap Oluştur
          </Link>
        </div>

        {/* Footer */}
        <p className={`text-center text-xs mt-6 ${isDark ? 'text-dark-muted' : 'text-light-muted'}`}>
          EmlakPro Danışman Paneli &copy; 2026
        </p>
      </div>
    </div>
  );
}